/**
 * Typography Scale System
 *
 * Standardized typography tokens following Material Design 3 guidelines
 * and WCAG 2.1 accessibility standards.
 *
 * Usage:
 * - Every page must have exactly ONE h1 element
 * - Follow proper heading hierarchy (h1 → h2 → h3, no skipping)
 * - Use the Heading component for consistent styling
 *
 * References:
 * - Material Design Typography: https://m3.material.io/styles/typography/overview
 * - WCAG 2.1 Section 1.3: https://www.w3.org/WAI/WCAG21/quickref/#adaptable
 */

export const typography = {
  /**
   * Display text - Used sparingly for marketing/landing pages
   */
  display: {
    1: 'text-5xl font-bold tracking-tight', // 48px - Hero sections
    2: 'text-4xl font-bold tracking-tight', // 36px - Large headings
  },

  /**
   * Heading levels - Primary content structure
   * Every page must have exactly ONE h1
   */
  heading: {
    1: 'text-2xl font-semibold tracking-tight', // 24px - Page titles (ONE per page)
    2: 'text-xl font-semibold', // 20px - Section titles
    3: 'text-lg font-semibold', // 18px - Card titles, subsections
    4: 'text-base font-semibold', // 16px - Small section headers
    5: 'text-sm font-semibold', // 14px - Inline headings
  },

  /**
   * Body text - Main content
   */
  body: {
    large: 'text-lg leading-relaxed', // 18px - Emphasis, intros
    base: 'text-base leading-normal', // 16px - Default body text
    small: 'text-sm leading-normal', // 14px - Secondary text, captions
    xs: 'text-xs leading-tight', // 12px - Fine print, metadata
  },

  /**
   * Label text - UI elements
   */
  label: {
    large: 'text-sm font-medium', // 14px - Form labels, buttons
    base: 'text-xs font-medium', // 12px - Small buttons, tags
    small: 'text-xs font-medium uppercase tracking-wide', // 12px - Table headers, badges
  },

  /**
   * Code and monospace - Technical content
   */
  code: {
    inline: 'text-sm font-mono', // 14px - Inline code
    block: 'text-sm font-mono leading-relaxed', // 14px - Code blocks
  },
} as const;

/**
 * Text color utilities for light and dark modes
 */
export const textColors = {
  primary: 'text-zinc-900 dark:text-zinc-100',
  secondary: 'text-zinc-600 dark:text-zinc-400',
  tertiary: 'text-zinc-500 dark:text-zinc-500',
  disabled: 'text-zinc-400 dark:text-zinc-600',
  inverse: 'text-white dark:text-zinc-900',
  link: 'text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300',
  error: 'text-red-600 dark:text-red-400',
  success: 'text-green-700 dark:text-green-400',
  warning: 'text-yellow-700 dark:text-yellow-400',
} as const;

/**
 * Typography guidelines and rules
 */
export const typographyRules = {
  // Heading hierarchy rules (for screen readers and SEO)
  headingHierarchy: [
    'Every page must have exactly ONE h1 element',
    'Headings must follow sequential order (h1 → h2 → h3)',
    'Never skip heading levels (e.g., h1 → h3 is wrong)',
    'Heading levels indicate document structure, not visual size',
  ],

  // Line length for readability
  lineLength: {
    optimal: 'max-w-2xl', // 672px - Optimal reading width (60-70 characters)
    wide: 'max-w-4xl', // 896px - Wide content
    full: 'max-w-full', // Full width for tables, etc.
  },

  // Contrast ratios for WCAG 2.1 compliance
  contrast: {
    normalText: '4.5:1', // Level AA minimum
    largeText: '3:0:1', // 18pt+ or 14pt+ bold
    enhanced: '7:1', // Level AAA
  },
} as const;

/**
 * Helper type for heading levels
 */
export type HeadingLevel = 1 | 2 | 3 | 4 | 5;

/**
 * Helper type for text variants
 */
export type TextVariant = keyof typeof typography;
export type BodySize = keyof typeof typography.body;
export type LabelSize = keyof typeof typography.label;
